import { i18n } from "@lingui/core";
import type { Messages } from "@lingui/core";

export const supportedLocales = [
  "en",
  "ko",
  "zh_CN",
  "ja",
  "th",
  "vi",
  "km",
  "tl",
  "id",
  "es",
  "pt_BR",
  "fr",
] as const;

export type SupportedLocale = (typeof supportedLocales)[number];
export const sourceLocale: SupportedLocale = "en";

export const intlLocales: Record<SupportedLocale, string> = {
  en: "en-US",
  ko: "ko-KR",
  zh_CN: "zh-CN",
  ja: "ja-JP",
  th: "th-TH",
  vi: "vi-VN",
  km: "km-KH",
  tl: "fil-PH",
  id: "id-ID",
  es: "es-ES",
  pt_BR: "pt-BR",
  fr: "fr-FR",
};

export function normalizeLocale(value: string | null | undefined): SupportedLocale | undefined {
  if (!value) return undefined;
  const normalized = value.trim().replaceAll("-", "_");
  const exact = supportedLocales.find(
    (locale) => locale.toLowerCase() === normalized.toLowerCase(),
  );
  if (exact) return exact;
  const language = normalized.split("_")[0]?.toLowerCase();
  return supportedLocales.find((locale) => locale.split("_")[0]?.toLowerCase() === language);
}

export function resolveLocale(cookieHeader?: string, acceptLanguage?: string): SupportedLocale {
  const cookie = cookieHeader
    ?.split(";")
    .map((part) => part.trim().split("="))
    .find(([name]) => name === "hl_locale")?.[1];
  const preferred = normalizeLocale(cookie ? decodeURIComponent(cookie) : undefined);
  if (preferred) return preferred;

  const requested = acceptLanguage
    ?.split(",")
    .map((part) => part.trim().split(";")[0])
    .map(normalizeLocale)
    .find((locale): locale is SupportedLocale => locale !== undefined);
  return requested ?? sourceLocale;
}

export function localeCookie(locale: SupportedLocale): string {
  return `hl_locale=${encodeURIComponent(locale)}; Path=/; Max-Age=31536000; Secure; HttpOnly; SameSite=Lax`;
}

export function activateLocale(locale: SupportedLocale, messages: Messages): void {
  i18n.load(locale, messages);
  i18n.activate(locale);
}

type CatalogModule = { readonly messages: Messages };
const catalogModules = import.meta.glob<CatalogModule>("../locales/*/messages.ts");

export async function loadCatalog(locale: SupportedLocale): Promise<Messages> {
  const match = Object.entries(catalogModules).find(([path]) =>
    path.includes(`/locales/${locale}/`),
  );
  if (!match) return {};
  const catalog = await match[1]();
  return catalog.messages;
}

export async function loadAndActivateLocale(locale: SupportedLocale): Promise<void> {
  activateLocale(locale, await loadCatalog(locale));
}

export { i18n };
